import {
  Flexbox,
  Icons,
  ITabsProps,
  Sizes,
  Tabs,
  Text,
  Tooltip,
} from '@sede-enterprise/shell-ds-react-framework';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { getDefaultPathForUser } from 'components/NavBar/NavBar';
import { Permissions } from 'context/constants';
import { useMemo, useState } from 'react';
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import styled from 'styled-components';
import { extractFirstSegmentFromUrl } from 'utils/url';

const MobileFooterContainer = styled.div`
  position: fixed;
  bottom: 0;
  width: 100vw;
  height: 72px;
  background: ${({ theme }) => theme.background.surface};
  padding: 0 24px;
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  z-index: 90;
  box-shadow: ${({ theme }) => theme.elevation.level2};

  & .shell-tabs-bottom {
    width: 100%;
  }

  & .shell-tabs-bottom .shell-tabs-nav {
    width: 100%;
  }

  & .shell-tabs-nav-wrap {
    width: 100%;
  }

  & .shell-tabs-nav-wrap .shell-tabs-nav-list {
    width: 100%;
    flex-direction: row;
    justify-content: stretch;
    align-items: center;

    .shell-tabs-tab {
      flex: 1 0 0;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

const { BarChartSolid, BarChart, Time } = Icons;

const availabilityUnavailableMessage =
  'Availability page is not available in Mobile mode.';

const UnavailableAvailabilityTab = () => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <Tooltip
      placement="top"
      isOpen={tooltipOpen}
      onOpenChange={setTooltipOpen}
      overlay={availabilityUnavailableMessage}
      trigger={
        <Flexbox
          columnGap="8px"
          alignItems="center"
          flexDirection="column"
          onClick={() => setTooltipOpen((current) => !current)}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
        >
          <Time data-testid="availability-disabled" />
          <Text size={Sizes.Small} bold prominence="strong">
            Availability
          </Text>
        </Flexbox>
      }
    />
  );
};

const routerTabItems = (
  company: string,
  currentTab: string,
  navigate: NavigateFunction,
): ITabsProps['items'] => [
  {
    key: 'optimisation',
    label: (
      <Flexbox
        columnGap="8px"
        alignItems="center"
        flexDirection={'column'}
        onClick={() => navigate(`/${company}/optimisation`)}
      >
        {currentTab === 'optimisation' ? (
          <BarChartSolid data-testid="optimisation-selected" />
        ) : (
          <BarChart data-testid="optimisation-not-selected" />
        )}
        <Text size={Sizes.Small} bold prominence={'strong'}>
          Optimisation
        </Text>
      </Flexbox>
    ),
  },
  {
    key: 'availability',
    disabled: true,
    label: <UnavailableAvailabilityTab />,
  },
];

export const MobileFooter = () => {
  const { company } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = useMemo(() => {
    return extractFirstSegmentFromUrl(location.pathname);
  }, [location]);

  const { permissions } = useAppUserDetailsFromJwt<Permissions>();
  const defaultPath = getDefaultPathForUser(permissions);

  return (
    <MobileFooterContainer data-testid="mobile-footer">
      <Tabs
        items={routerTabItems(
          company as string,
          currentTab ?? defaultPath,
          navigate,
        )}
        tabPosition={'bottom'}
      />
    </MobileFooterContainer>
  );
};
